import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  DepositLocation,
  LocationTypeEnum,
  Query,
} from '@varo/graphql-schema-types';
import { LocationObject } from 'expo-location';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useDepositCashFlowFromRoute } from 'features/moveMoney/hooks/useDepositCashFlowFromRoute';
import { useFeeDisplay } from 'features/moveMoney/hooks/useFeeDisplay';
import { CVS_RETAILER_KEY } from 'features/moveMoney/screens/depositCash/constants';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import GetAtmPoints from 'services/graphql/queries/GetAtmPoints';
import PredictionMapView from 'shared/components/maps/PredictionMapView/PredictionMapView';
import { PlaceDetailsResponse } from 'shared/components/maps/PredictionMapView/PredictionMapView.helpers';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isAndroid } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { DefaultMapZoom } from 'shared/utils/maps/mapConstants';
import openMarkerInMaps from 'shared/utils/maps/openMarkerInMaps';
import { useQuery } from 'urql';

function MapScreen() {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'depositCash',
  });

  const router = useRouter();
  const params = useLocalSearchParams<{ flowSource?: string }>();
  const { flowName } = useDepositCashFlowFromRoute();

  const { segmentClient } = useAnalyticsConfiguration();
  const hasLoggedScreenEventRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasLoggedScreenEventRef.current) {
        return;
      }

      hasLoggedScreenEventRef.current = true;

      void segmentClient.logScreenEvent('Deposit Locations', {
        name: 'Deposit Locations',
        category: 'deposit_cash',
        subcategory: 'barcode',
        flow_name: flowName ?? 'move_money',
      });

      return () => {
        hasLoggedScreenEventRef.current = false;
      };
    }, [flowName, segmentClient]),
  );

  const isKrogerBarcodeEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENABLE_GD_BARCODE_KROGER,
  );

  const [selectedMarker, setSelectedMarker] = useState<DepositLocation | null>(
    null,
  );

  const feeDisplay = useFeeDisplay(selectedMarker, translatedText);

  const pointDetailsBottomSheetRef = useRef<BottomSheetModal>(null);
  const shouldRestoreBottomSheet = useRef(false);
  const lastCameraLocationRef = useRef<google.maps.LatLngLiteral | undefined>(
    undefined,
  );

  const [coordinates, setCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [{ data: atmPointsResponse, fetching: isAtmPointsFetching }] =
    useQuery<Query>({
      query: GetAtmPoints(LocationTypeEnum.Deposit),
      variables: coordinates ?? undefined,
      pause: !coordinates,
      requestPolicy: 'cache-and-network',
    });
  const [mapScreenRefreshKey, setMapScreenRefreshKey] = useState(0);
  const [showCvsLocation, setShowCvsLocation] = useState(false);
  const [searchBarValue, setSearchBarValue] = useState('');

  const selectedMarkerAddress = selectedMarker?.address
    ? [
        selectedMarker.address.addressLine1,
        selectedMarker.address.city,
        selectedMarker.address.state && selectedMarker.address.zip
          ? `${selectedMarker.address.state} ${selectedMarker.address.zip}`
          : (selectedMarker.address.state ?? selectedMarker.address.zip),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const shouldShowKrogerFeeNote =
    isKrogerBarcodeEnabled && !!selectedMarker?.isBarcodeFreeDepositLocation;

  const triggerMapScreenReload = () => {
    if (isAndroid) {
      setMapScreenRefreshKey((prevKey) => prevKey + 1);
    }
  };

  const handlePredictionSelected = (placeDetails: PlaceDetailsResponse) => {
    const lat = parseFloat(placeDetails?.result?.geometry?.location?.lat);
    const lng = parseFloat(placeDetails?.result?.geometry?.location?.lng);

    lastCameraLocationRef.current = { lat, lng };
    setCoordinates({ latitude: lat, longitude: lng });
  };

  const getLatLng = (location: LocationObject | google.maps.LatLngLiteral) => {
    return 'coords' in location
      ? {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        }
      : { latitude: location.lat, longitude: location.lng };
  };

  const handleOnLocationChange = useCallback(
    (
      location: LocationObject | google.maps.LatLngLiteral,
      isInitialLoad?: boolean,
    ) => {
      const { latitude, longitude } = getLatLng(location);

      lastCameraLocationRef.current = { lat: latitude, lng: longitude };

      if (isInitialLoad) {
        setCoordinates({ latitude, longitude });
      }
    },
    [],
  );

  const handleDepositPinClickedAnalytics = usePressableAnalytics({
    analyticsId: 'locationPinClicked',
    customAttributes: {
      deposit_location_name: selectedMarker?.name ?? '',
      deposit_location_zip_code: selectedMarker?.address?.zip ?? '',
      deposit_location_address: selectedMarker?.address?.addressLine1 ?? '',
    },
  });

  useEffect(() => {
    if (selectedMarker?.id) {
      handleDepositPinClickedAnalytics();
    }
  }, [handleDepositPinClickedAnalytics, selectedMarker?.id]);

  const depositLocations = atmPointsResponse?.depositLocations ?? [];
  const markerData = showCvsLocation
    ? depositLocations.filter((location) => location.isFreeCashDepositLocation)
    : depositLocations;

  const displayMarkerData = useMemo(
    () =>
      markerData.map((location) => ({
        ...location,
        isCvsMarker: Number(location.retailerKey) === CVS_RETAILER_KEY,
      })),
    [markerData],
  );

  const getMarkerWithFullDetails = useCallback(
    (point: DepositLocation) => {
      if (!point?.id) {
        return point;
      }

      const markerFromState = markerData.find(
        (location) => location.id === point.id,
      );

      return markerFromState ?? point;
    },
    [markerData],
  );

  const handleMarkerPress = useCallback(
    (point: DepositLocation) => {
      const markerWithFullDetails = getMarkerWithFullDetails(point);

      setSelectedMarker(markerWithFullDetails);
      pointDetailsBottomSheetRef.current?.present();
    },
    [getMarkerWithFullDetails],
  );

  const handleBottomSheetDismiss = useCallback(() => {
    if (shouldRestoreBottomSheet.current) {
      return;
    }

    setSelectedMarker(null);
  }, []);

  const handleOpenInMapsAnalytics = usePressableAnalytics({
    analyticsId: 'openInMaps',
    customAttributes: {
      source_name: 'Deposit Locations',
      category: 'deposit_cash',
      subcategory: 'barcode',
      flow_name: flowName,
    },
  });

  const handleUserTypesOnSearchBarAnalytics = usePressableAnalytics({
    analyticsId: 'typesOnSearchBar',
    customAttributes: {
      location_entered: searchBarValue,
    },
  });

  const handleOpenInMaps = () => {
    if (selectedMarker) {
      openMarkerInMaps(selectedMarker);
      handleOpenInMapsAnalytics();
    }
  };

  const handleShowBarcodeClickedAnalytics = usePressableAnalytics({
    analyticsId: 'showBarcodeClicked',
    customAttributes: {
      source_name: 'deposit_cash_barcode',
      category: 'deposit_cash',
      subcategory: 'barcode',
      flow_name: flowName,
    },
  });

  const handleShowBarcode = (): void => {
    handleShowBarcodeClickedAnalytics();

    if (selectedMarker) {
      shouldRestoreBottomSheet.current = true;
      pointDetailsBottomSheetRef.current?.dismiss();

      router.push({
        pathname: '/(authenticated)/move-money/deposit-cash/barcode',
        params: {
          id: selectedMarker.id ?? '',
          retailerKey: selectedMarker.retailerKey ?? '',
          name: selectedMarker.name ?? '',
          type: selectedMarker.type ?? '',
          phoneNumber: selectedMarker.phoneNumber ?? '',
          distance: selectedMarker.distance ?? '',
          latitude: selectedMarker.coordinate?.latitude?.toString() ?? '',
          longitude: selectedMarker.coordinate?.longitude?.toString() ?? '',
          customerFee: selectedMarker.customerFee?.toString() ?? '',
          isFreeCashDepositLocation:
            selectedMarker.isFreeCashDepositLocation?.toString() ?? '',
          isBarcodeFreeDepositLocation:
            selectedMarker.isBarcodeFreeDepositLocation?.toString() ?? '',
          addressLine1: selectedMarker.address?.addressLine1 ?? '',
          addressLine2: selectedMarker.address?.addressLine2 ?? '',
          city: selectedMarker.address?.city ?? '',
          state: selectedMarker.address?.state ?? '',
          zip: selectedMarker.address?.zip ?? '',
          flowSource: params.flowSource || 'move_money',
        },
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (shouldRestoreBottomSheet.current && selectedMarker) {
        // Reload map on Android to ensure it renders properly behind bottom sheet
        triggerMapScreenReload();
        pointDetailsBottomSheetRef.current?.present();
        shouldRestoreBottomSheet.current = false;
      }
    }, [selectedMarker]),
  );

  useEffect(() => {
    if (searchBarValue) {
      handleUserTypesOnSearchBarAnalytics();
    }
  }, [handleUserTypesOnSearchBarAnalytics, searchBarValue]);

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <PredictionMapView
        key={mapScreenRefreshKey}
        navigationBarTitle=""
        onPredictionSelected={handlePredictionSelected}
        onMarkerPress={handleMarkerPress}
        isLoading={isAtmPointsFetching}
        onLocationChange={handleOnLocationChange}
        markerData={displayMarkerData}
        requestLocationTitle={translatedText('maps.requestLocationTitle')}
        requestLocationBody={translatedText('maps.requestLocationBody')}
        defaultZoom={DefaultMapZoom.s}
        initialCameraLocation={lastCameraLocationRef.current}
        selectedLocationId={selectedMarker?.id}
        filterComponent={
          <View style={styles.filterComponentWrapper}>
            <ListItem
              primaryCopy={translatedText('maps.freeCashDepositLocations')}
              showsChevron={false}
              trailingAccessory={{
                id: 'CVSSwitch',
                name: VXLComponentId.SWITCH,
                props: {
                  isOn: showCvsLocation,
                  onValueChange: (value: boolean) => setShowCvsLocation(value),
                  analyticsId: showCvsLocation
                    ? 'freeCashDepositLocationsTurnedOff'
                    : 'freeCashDepositLocationsTurnedOn',
                },
              }}
            />
          </View>
        }
        onSearchValueChange={(value) => {
          setSearchBarValue(value);
        }}
      />

      <BottomSheet
        ref={pointDetailsBottomSheetRef}
        onDismiss={handleBottomSheetDismiss}
      >
        {selectedMarker && (
          <>
            <TitleLgText style={styles.title}>
              {selectedMarker.name}
            </TitleLgText>
            <ListItem
              primaryCopy={selectedMarkerAddress}
              secondaryCopy={selectedMarker.distance}
              showsChevron={false}
              trailingAccessory={
                feeDisplay
                  ? {
                      id: 'feeTag',
                      name: VXLComponentId.TAG,
                      props: {
                        text: feeDisplay.text,
                        type: feeDisplay.type,
                      },
                    }
                  : null
              }
            />
            {shouldShowKrogerFeeNote ? (
              <BodyXSText style={styles.krogerFeeNote}>
                {translatedText('maps.krogerFeeNote')}
              </BodyXSText>
            ) : null}
            <View style={styles.buttonContainer}>
              <Button
                label={translatedText('maps.showBarcode')}
                type="PRIMARY"
                onPress={handleShowBarcode}
              />
              <Button
                label={translatedText('maps.openInMaps')}
                type="SECONDARY"
                onPress={handleOpenInMaps}
              />
            </View>
          </>
        )}
      </BottomSheet>
    </>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  filterComponentWrapper: {
    marginBottom: -theme.spacing.m,
    marginTop: -theme.spacing.m,
  },
  title: {
    paddingHorizontal: theme.spacing.m,
  },
  krogerFeeNote: {
    color: theme.color.text.secondary,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'left',
  },
});

export default MapScreen;
